import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import Layout from '../componentes/Layout';
import BuscaPecas from '../componentes/BuscaPecas';
import BuscaGuiada from '../componentes/BuscaGuiada';
import BuscaPlaca from '../componentes/BuscaPlaca';
import NavegarCatalogo from '../componentes/NavegarCatalogo';
import { reposicao } from '../lib/consultas';
import { useSessao, podeGerenciarCatalogo } from '../lib/sessao';

// Tela inicial (WEB_PLAN §2.5, tela 2). Abrir o app já é estar buscando.
//
// Três modos, mesma tela e mesmo cartão de resultado:
//   Livre   campo único — código sujo, EAN, nome, veículo (o padrão do balcão)
//   Veículo cascata montadora → modelo → ano → motor → categoria, como no
//           portal dos fornecedores: cada escolha libera a próxima
//   Placa   digitou a placa, o carro aparece — base construída pela própria loja
//   Tipo    "Calotas → Fiat → Palio" (01/10/2026, ADR-009)
//   Carro   "Fiat → Palio → Óleo" — e o óleo certo, pelo que o carro pede

type Modo = 'livre' | 'tipo' | 'carro' | 'veiculo' | 'placa';

const MODOS: Array<{ id: Modo; rotulo: string }> = [
  { id: 'livre', rotulo: 'Livre' },
  { id: 'tipo', rotulo: 'Por tipo' },
  { id: 'carro', rotulo: 'Por carro' },
  { id: 'veiculo', rotulo: 'Por versão' },
  { id: 'placa', rotulo: 'Por placa' },
];

export default function Busca() {
  const navigate = useNavigate();
  const { papel } = useSessao();
  const [modo, setModo] = useState<Modo>('livre');

  // Só para gerente/dono: vendedor não precisa de painel, precisa de campo.
  const { data: faltando } = useQuery({
    queryKey: ['reposicao', 'resumo'],
    queryFn: reposicao,
    enabled: podeGerenciarCatalogo(papel),
    staleTime: 5 * 60_000,
  });

  const abrirPeca = (p: { product_id: string }) => navigate(`/peca/${p.product_id}`);
  // A folha "Onde fica" (06/10/2026) abre por cima da busca; dela, a ficha.
  const abrirAFicha = { rotulo: 'Abrir a ficha', fazer: abrirPeca };

  return (
    <Layout
      titulo="Buscar peça"
      acao={
        podeGerenciarCatalogo(papel) ? (
          <Link to="/cadastro" className="botao-secundario min-h-11 px-3 py-2 text-sm lg:min-h-9">
            + Peça
          </Link>
        ) : null
      }
    >
      {/* O jeito de procurar: o seletor segmentado do Grafite (o ativo é o
          degrau de cinza, não uma cor), com 44 px de alvo no celular. */}
      <div className="mb-4 grid grid-cols-3 gap-1 rounded-[0.625rem] border border-fio bg-panel p-[3px] sm:grid-cols-5">
        {MODOS.map((m) => (
          <button
            key={m.id}
            type="button"
            onClick={() => setModo(m.id)}
            aria-pressed={modo === m.id}
            className={[
              'min-h-11 rounded-lg px-3 text-sm font-medium transition-colors sm:min-h-9',
              modo === m.id ? 'bg-edge text-paper' : 'text-mute hover:text-paper',
            ].join(' ')}
          >
            {m.rotulo}
          </button>
        ))}
      </div>

      {/* O chip de fornecedor é dado comercial: mesma régua de "Onde comprar"
          na ficha da peça. */}
      {modo === 'livre' && (
        <BuscaPecas onEscolher={abrirPeca} mostrarFornecedores={podeGerenciarCatalogo(papel)}
                    mostrarSimilares acaoNoOndeFica={abrirAFicha} buscarEmDestaque />
      )}
      {modo === 'tipo' && <NavegarCatalogo key="tipo" comeco="tipo" onEscolher={abrirPeca} acaoNoOndeFica={abrirAFicha} />}
      {modo === 'carro' && <NavegarCatalogo key="carro" comeco="carro" onEscolher={abrirPeca} acaoNoOndeFica={abrirAFicha} />}
      {modo === 'veiculo' && <BuscaGuiada onEscolher={abrirPeca} acaoNoOndeFica={abrirAFicha} />}
      {modo === 'placa' && <BuscaPlaca onEscolher={abrirPeca} acaoNoOndeFica={abrirAFicha} />}

      {podeGerenciarCatalogo(papel) && faltando && faltando.length > 0 && (
        <Link
          to="/relatorios"
          className="cartao mt-6 flex min-h-11 items-center justify-between gap-3 px-4 py-3 text-sm transition-colors hover:bg-panel-alt"
        >
          {/* O sinal no PONTO; o bloco fica no cinza do cartão (Grafite). */}
          <span className="flex items-center gap-2 text-paper">
            <span className="ponto text-alerta" aria-hidden="true" />
            <span>
              <strong className="numero font-medium">{faltando.length}</strong>{' '}
              {faltando.length === 1 ? 'item' : 'itens'} abaixo do ponto de pedido
            </span>
          </span>
          <span className="text-mute" aria-hidden="true">→</span>
        </Link>
      )}
    </Layout>
  );
}
